import React, { useEffect, useState } from "react";
import { fetchInstagramFeed } from "../api";
import { Instagram, Play, Loader2 } from "lucide-react";

const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchInstagramFeed()
      .then((res) => {
        if (cancelled) return;
        setConfigured(!!res.configured);
        setPosts(res.posts || []);
      })
      .catch(() => {
        if (!cancelled) {
          setConfigured(false);
          setPosts([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Hide entire section when Instagram isn't configured yet — homepage stays clean
  if (!loading && (!configured || posts.length === 0)) return null;

  return (
    <section className="bg-[var(--ivory)] py-20 lg:py-28" data-testid="instagram-feed">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--copper)] mb-4">— From the studio</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.1] text-[var(--espresso)]">
              Follow the <em className="italic text-[var(--copper)]">making</em>.
            </h2>
          </div>
          <a
            href="https://www.instagram.com/venus3dcreations/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--copper)] vc-link-underline self-start md:self-end"
          >
            <Instagram className="w-4 h-4" /> @venus3dcreations
          </a>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--copper)]" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
            {posts.map((p) => (
              <a
                key={p.id}
                href={p.permalink}
                target="_blank"
                rel="noreferrer"
                data-testid={`ig-post-${p.id}`}
                className="group relative block aspect-square overflow-hidden bg-[var(--sand)]"
              >
                <img
                  src={p.media_url}
                  alt={p.caption || "Venus 3D Creations Instagram post"}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[var(--espresso)]/0 group-hover:bg-[var(--espresso)]/40 transition-colors duration-300 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {p.media_type === "VIDEO" && (
                  <span className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                    <Play className="w-3 h-3 text-white" fill="white" />
                  </span>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InstagramFeed;
